export interface User {
  name: string;
  userId: MongoId;
  /** `id` to fetch avatar */
  avatar: MongoId;
}

export enum CreateCoursePermission {
  NOT_ALLOWED,
  ALLOWED,
  PROVISIONAL,
}

export interface UserRegistration {
  userId: MongoId;
  name: string;
  email: string;
  avatar: MongoId;
  /** No trailing slashes */
  cluster: string;
  s3BucketURL: BucketURL;
  userExists: boolean;
  canCreateCourses: CreateCoursePermission;
  profileCompleted: boolean;
  university: {
    name: string;
    slug: string;
  };
  verified: boolean;
}

export interface AuthCredentials {
  token?: string;
  socket?: {
    key: string;
    cluster: string;
  };
}

export interface UserSession extends UserRegistration, AuthCredentials {}

export interface FileAttachment {
  /**
   * filename as stored on s3 along with extension
   * @example 'some-random-id.pdf'
   */
  name: string;
  /**
   * file extension as uploaded by the creator
   * @example 'pdf'
   */
  extension: string;
  /**
   * filename as uploaded by the creator without the extension
   * @example 'OB Case Study 3'
   */
  originalName: string;
}

export interface AttachmentRequestConfig<T> {
  data?: T;
  headers?: Record<string, string>;
  /**
   * @default 'POST'
   */
  method?: 'GET' | 'POST';
  url: string;
}

export enum CourseRole {
  ADMIN = 'admin',
  INSTRUCTOR = 'instructor',
}

export type CourseUserWithRole<T extends CourseRole> = User & { role: T };

export type CourseUser = CourseUserWithRole<CourseRole>;

export enum ClassActivityTypes {
  POLL = 'poll',
  QUIZ = 'quiz',
  DISCUSSION = 'discussion',
  RESOURCE = 'resource',
}

export interface BaseActivity {
  id: MongoId;
  courseId: MongoId;
  /**
   * `null` means title is not set.
   * safeEmptyString (placeholder) should also be considered empty string.
   * */
  title: string | null;
  /** `-1` when not edited */
  editedOn: UnixTime;
  /** `null` when not edited */
  editedBy: CourseUser | null;
  createdOn: UnixTime;
  createdBy: CourseUser;
  /** @deprecated never used in ppt addon */
  comments: undefined;
  /** @deprecated never used in ppt addon */
  nodeType: undefined;
}

export enum ToBeDone {
  IN_CLASS = 'inClass',
  PRE_CLASS = 'preClass',
}

export enum DueDateType {
  AUTO = 'auto',
  MANUAL = 'manual',
}

export enum ActivityDuration {
  /** Activity will be closed by end of class */
  BY_END_OF_THE_CLASS = -2,
  /** Activity should be stopped manually */
  MANUALLY_STOPPED = -1,
  /** Useful for pre-class activities */
  NOT_APPLICABLE = 0,
  AFTER_30_SECONDS = 0.5,
  AFTER_1_MINUTE = 1,
  AFTER_2_MINUTES = 2,
  AFTER_5_MINUTES = 5,
  AFTER_10_MINUTES = 10,
  AFTER_15_MINUTES = 15,
  AFTER_20_MINUTES = 20,
  AFTER_30_MINUTES = 30,
  AFTER_45_MINUTES = 45,
  AFTER_1_HOUR = 60,
}

export interface PublishPreferences {
  /** Activity to be done: `preClass` or `inClass` */
  toBeDone: ToBeDone;
  /** Activity closing mode: `auto` or `manual` */
  dueDateType: DueDateType;
  /** Auto close activity in specified duration */
  duration: ActivityDuration;
  /** If set, then submissions will be anonymous */
  isAnonymous: boolean;
  /** If set, allow late submissions */
  allowLate: boolean;
  /** If set, show results after deadline */
  deadlineFirst: boolean;
}

export interface BasePPTOptions {
  isAddedToPPT: boolean;
}

export enum PollSlideTypes {
  QUICK_POLL = 'quick_poll',
  BAR_GRAPH = 'bar_graph',
  PIE_CHART = 'pie_chart',
  WORD_CLOUD = 'word_cloud',
}

export interface GetPreSignedURLRequestConfig {
  url: string;
  /**
   * Request body payload except `originalFileName` and
   * `fileType`
   */
  data?: Record<string, unknown>;
  headers?: Record<string, string>;
}

export interface PreSignedURLResponse {
  /** Pre-signed url of S3 */
  url: string;
  /** Name of file in S3 bucket */
  name: string;
  /** Extension of uploaded file */
  type: string;
  /** MIME type of the uploaded file */
  contentType: string;
}
